import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// NEW BRAND COLORS - Based on Brand Guide
export const BRAND_COLORS = {
  // Authority, precision
  authority: {
    black: "#000000", // Black
    charcoal: "#1F1F1F", // Charcoal
  },

  // Innovation, acceleration
  innovation: {
    electricBlue: "#005293", // PMS 301 - Electric Blue
    citron: "#C1BB00", // PMS 397 - Citron
    green: "#34B233", // PMS 361 - Green
  },

  // Clinical confidence
  clinical: {
    white: "#FFFFFF", // White
    titaniumGrey: "#4D4F53", // PMS Cool Grey 11 - Titanium Grey
    lightGrey: "#D5D6D2", // PMS Cool Grey 2 - Light Grey
  },

  // Disruption, energy
  disruption: {
    orange: "#FF6319", // PMS 165 - Orange
  },

  // Logo colors
  logo: {
    green: "#34B233", // PMS 361 - Green
    electricBlue: "#005293", // PMS 301 - Electric Blue
  },
} as const;

// Card Color Constants - Updated with new brand colors
export const CARD_COLORS = {
  // Service Colors - Updated with new brand palette
  service: {
    businessWebsite: {
      normal: "#005293", // Electric Blue
      hover: "#003d6b", // Darker Electric Blue
      active: "#002a47", // Darkest Electric Blue
    },
    compoundPharmacy: {
      normal: "#34B233", // Brand Green
      hover: "#2a8f29", // Darker Green
      active: "#1f6b1e", // Darkest Green
    },
    ehrPaymentProcessor: {
      normal: "#C1BB00", // Citron
      hover: "#9a9600", // Darker Citron
      active: "#737100", // Darkest Citron
    },
    telemedNationwide: {
      normal: "#FF6319", // Brand Orange
      hover: "#e5571a", // Darker Orange
      active: "#cc4a17", // Darkest Orange
    },
  },
  // Value/Feature Card Colors - Updated with new brand palette
  values: {
    red: "#FF6319", // Brand Orange (Disruption/Energy)
    blue: "#005293", // Electric Blue (Innovation)
    green: "#34B233", // Brand Green (Innovation)
    purple: "#4D4F53", // Titanium Grey (Clinical)
    orange: "#FF6319", // Brand Orange (Disruption)
    teal: "#C1BB00", // Citron (Innovation)
  },
} as const;

// OLD BRAND COLORS - Commented out for reference
/*
export const OLD_CARD_COLORS = {
  // Service Colors
  service: {
    businessWebsite: {
      normal: '#3B82F6',
      hover: '#2563EB',
      active: '#1E40AF'
    },
    compoundPharmacy: {
      normal: '#16A34A',
      hover: '#15803D',
      active: '#166534'
    },
    ehrPaymentProcessor: {
      normal: '#9333EA',
      hover: '#7E22CE',
      active: '#6B21A8'
    },
    telemedNationwide: {
      normal: '#F97316',
      hover: '#EA580C',
      active: '#C2410C'
    }
  },
  // Value/Feature Card Colors
  values: {
    red: '#EF4444',      // Patient-Centered Innovation
    blue: '#3B82F6',     // Unwavering Integrity
    green: '#16A34A',    // Relentless Compliance
    purple: '#9333EA',   // Continuous Innovation
    orange: '#F97316',   // Collaborative Partnership
    teal: '#0D9488'      // Inclusive Access
  }
};
*/

// General Website Colors - Updated with new brand palette
export const WEBSITE_COLORS = {
  // Electric Blue (Primary Brand Color - PMS 301)
  electricBlue: {
    50: "#e6f2ff", // Lightest Electric Blue
    100: "#cce5ff", // Very Light Electric Blue
    200: "#99ccff", // Light Electric Blue
    300: "#66b3ff", // Medium Light Electric Blue
    400: "#3399ff", // Medium Electric Blue
    500: "#005293", // Electric Blue (Primary - PMS 301)
    600: "#004275", // Darker Electric Blue
    700: "#003157", // Dark Electric Blue
    800: "#002139", // Very Dark Electric Blue
    900: "#00101c", // Darkest Electric Blue
  },

  // Brand Green (PMS 361)
  green: {
    50: "#e8f8e8", // Lightest Green
    100: "#d1f1d1", // Very Light Green
    200: "#a3e3a3", // Light Green
    300: "#75d575", // Medium Light Green
    400: "#47c747", // Medium Green
    500: "#34B233", // Brand Green (Primary - PMS 361)
    600: "#2a8f29", // Darker Green
    700: "#1f6b1e", // Dark Green
    800: "#154814", // Very Dark Green
    900: "#0a240a", // Darkest Green
  },

  // Grays (Clinical Confidence Colors)
  gray: {
    50: "#FFFFFF", // White
    100: "#f5f5f5", // Very Light Gray
    200: "#D5D6D2", // Light Grey (PMS Cool Grey 2)
    300: "#b8b9b5", // Medium Light Gray
    400: "#9b9c98", // Medium Gray
    500: "#7e7f7b", // Medium Dark Gray
    600: "#61625e", // Dark Gray
    700: "#4D4F53", // Titanium Grey (PMS Cool Grey 11)
    800: "#3a3c40", // Very Dark Gray
    900: "#000000", // Black/Charcoal
  },

  // Citron (PMS 397)
  citron: {
    50: "#fefee6", // Lightest Citron
    100: "#fdfdcc", // Very Light Citron
    200: "#fbfb99", // Light Citron
    300: "#f9f966", // Medium Light Citron
    400: "#f7f733", // Medium Citron
    500: "#C1BB00", // Citron (Primary - PMS 397)
    600: "#9a9600", // Darker Citron
    700: "#737100", // Dark Citron
    800: "#4d4b00", // Very Dark Citron
    900: "#262600", // Darkest Citron
  },

  // Orange (PMS 165)
  orange: {
    50: "#fff2e6", // Lightest Orange
    100: "#ffe5cc", // Very Light Orange
    200: "#ffcc99", // Light Orange
    300: "#ffb266", // Medium Light Orange
    400: "#ff9933", // Medium Orange
    500: "#FF6319", // Brand Orange (Primary - PMS 165)
    600: "#e5571a", // Darker Orange
    700: "#cc4a17", // Dark Orange
    800: "#b23e14", // Very Dark Orange
    900: "#993211", // Darkest Orange
  },

  // Text Colors (updated with new brand colors)
  text: {
    primary: "#000000", // Black (Authority)
    secondary: "#4D4F53", // Titanium Grey (Clinical)
    muted: "#D5D6D2", // Light Grey (Clinical)
    light: "#D5D6D2", // Light Grey
    white: "#FFFFFF", // White
    blue: "#005293", // Electric Blue
    green: "#34B233", // Brand Green
    orange: "#FF6319", // Brand Orange
    citron: "#C1BB00", // Citron
  },

  // Background Colors (updated with new brand colors)
  background: {
    white: "#FFFFFF", // White (Clinical)
    black: "#000000", // Black (Authority)
    charcoal: "#1F1F1F", // Charcoal (Authority)
    lightGray: "#D5D6D2", // Light Grey (Clinical)
    darkGray: "#4D4F53", // Titanium Grey (Clinical)
  },
} as const;

// OLD WEBSITE COLORS - Commented out for reference
/*
export const OLD_WEBSITE_COLORS = {
  // Blues (Primary Brand Colors)
  blue: {
    50: '#EFF6FF',   // bg-blue-50
    100: '#DBEAFE',  // bg-blue-100
    200: '#BFDBFE',  // bg-blue-200
    300: '#93C5FD',  // bg-blue-300
    400: '#60A5FA',  // bg-blue-400
    500: '#3B82F6',  // bg-blue-500 (Primary)
    600: '#2563EB',  // bg-blue-600
    700: '#1D4ED8',  // bg-blue-700
    800: '#1E40AF',  // bg-blue-800
    900: '#1E3A8A'   // bg-blue-900
  },
  
  // Grays (Neutral Colors)
  gray: {
    50: '#F9FAFB',   // bg-gray-50
    100: '#F3F4F6',  // bg-gray-100
    200: '#E5E7EB',  // bg-gray-200
    300: '#D1D5DB',  // bg-gray-300
    400: '#9CA3AF',  // bg-gray-400
    500: '#6B7280',  // bg-gray-500
    600: '#4B5563',  // bg-gray-600
    700: '#374151',  // bg-gray-700
    800: '#1F2937',  // bg-gray-800
    900: '#111827'   // bg-gray-900
  },
  
  // Text Colors (commonly used)
  text: {
    primary: '#111827',    // text-gray-900
    secondary: '#4B5563',  // text-gray-600
    muted: '#6B7280',      // text-gray-500
    light: '#9CA3AF',      // text-gray-400
    white: '#FFFFFF',      // text-white
    blue: '#2563EB',       // text-blue-600
    blueLight: '#3B82F6'   // text-blue-500
  },
  
  // Background Colors
  background: {
    white: '#FFFFFF',      // bg-white
    black: '#000000',      // bg-black
    lightGray: '#F9FAFB',  // bg-gray-50
    darkGray: '#1F2937'    // bg-gray-800
  }
};
*/

/**
 * @deprecated Use `CARD_COLORS.values` directly instead. This legacy export will be removed in a future release.
 * Legacy color mapping for backward compatibility.
 */

export const cardColorMap = CARD_COLORS.values;

// Standardized Padding Constants
export const padding = {
  // Section padding for main content areas
  section: {
    mobile: "p-16", // Mobile: 32px (2rem)
    desktop: "md:p-20", // Desktop: 48px (3rem)
    combined: "p-16 md:p-20", // Combined mobile + desktop
  },

  // Card padding for components like cards, modals, etc.
  card: {
    small: "p-4", // Small cards: 16px (1rem)
    medium: "p-6", // Medium cards: 24px (1.5rem)
    large: "p-8", // Large cards: 32px (2rem)
    extraLarge: "p-12", // Extra large cards: 48px (3rem)
  },

  // Container padding for wrapper elements
  container: {
    mobile: "px-4", // Mobile horizontal: 16px
    tablet: "sm:px-6", // Tablet horizontal: 24px
    desktop: "lg:px-8", // Desktop horizontal: 32px
    combined: "px-4 sm:px-6 lg:px-8", // Combined responsive
  },

  // Vertical spacing for sections
  vertical: {
    small: "py-8", // Small vertical: 32px
    medium: "py-12", // Medium vertical: 48px
    large: "py-16", // Large vertical: 64px
    extraLarge: "py-20", // Extra large vertical: 80px
  },
} as const;

// Logo Configuration - Centralized logo paths and dimensions
export const LOGOS = {
  header: "/images/logo-inversed.svg",
  footer: "/images/logo-subtitle.svg",
} as const;

export const LOGO_DIMENSIONS = {
  header: {
    width: 154,
    height: 38,
  },
  footer: {
    width: 240,
    height: 60,
  },
} as const;

export const CRITICAL_IMAGES = [
  LOGOS.header,
  "/images/axentra-og.jpg",
] as const;

// External Links - Centralized URLs for easy updates
export const EXTERNAL_LINKS = {
  calendly: "https://calendly.com/bradley-axentrahealth",
} as const;
